import { useCallback, useMemo } from 'react';

import { useDialogContext } from '../../../../contexts/DialogContext';
import { window } from '../../../../globals';
import { useAsyncRequest } from '../../../../hooks';
import { get, post } from '../../../../redux/api';
import type { SlackProfileResponse } from '../../../../../shared/model-typings';
import type { OrganizationSettingsEndpoint } from '../../shared/useOrganizationSettings';

type SlackSettings = {
  slackSettings: SlackProfileResponse[] | null;
  onDisconnect: (channel: string) => void;
  isLoading: boolean;
  errors: string[] | null;
};

/**
 * Hook for fetching the slack settings for a given org
 */
function useSlackSettings(): SlackSettings {
  const endpoint = useMemo<OrganizationSettingsEndpoint>(() =>
    `/api/organizations/${window.user?.organizationId ?? 0}/settings/integrations/slack`, []);
  //  Note that we wrap this in a useCallback because useAsyncRequest will re-fetch if the request function changes.
  const fetchSettings = useCallback(() => get<SlackProfileResponse[]>(endpoint), [endpoint]);
  const { value: slackSettings, isLoading, errors } = useAsyncRequest(fetchSettings);

  // Modal for deleting slack notifications
  const { onConfirm } = useDialogContext();

  const onDisconnect = (channel: string) => {
    onConfirm({
      title: 'Remove Slack Channel?',
      body: `Are you sure you want to disassociate the current organization with the registered Slack account?`,
      confirmText: 'Remove channel',
      onConfirm: async () => {
        await post(
          '/api/services/slack/disconnectService',
          {
            channel,
          },
        );
        window.location.reload();
      },
    });
  };

  return {
    slackSettings,
    isLoading,
    errors,
    onDisconnect,
  };
}

export default useSlackSettings;
